// League settings' rivals (PLAN-2026-10-29-the-schedule-before-the-season §4,
// scenario B4): the team each plays in a rivalry week, a menu beside every
// team, under the rivalry weeks.
//
// NOT A RULE BUT THE TEAMS' OWN, as where a team plays is
// (`teams.rival_team_id`), so a pick saves at once, as the division menus do,
// and both teams of the pair at once. A team that already has a rival says
// so in the menu, since picking it ends that pairing.

import { useState } from 'react';
import type { RulesTeam } from '@fsp/domain';
import { Caption, Choice, Text, tell } from '../../kit/ui';
import type { VoteOutcome } from '../../data/rules';

/** The menu's value for no rival: a team id is never this. */
const NONE = '__none__';

export function RivalsPlacement({
  teams,
  canName,
  onName,
}: {
  /** Every team in seat order with its rival (`LeagueRulesView.teams`). */
  teams: readonly RulesTeam[];
  /** A commissioner, before the season opens. */
  canName: boolean;
  onName: (teamId: string, rivalTeamId: string | null) => Promise<VoteOutcome>;
}) {
  const [naming, setNaming] = useState<string | null>(null);
  const nameOf = (id: string) => teams.find((t) => t.id === id)?.name ?? id;

  const name = async (team: RulesTeam, rivalId: string | null) => {
    setNaming(team.id);
    const out = await onName(team.id, rivalId);
    setNaming(null);
    if (out.ok)
      tell(
        rivalId === null
          ? `${team.name} has no rival.`
          : `${team.name} and ${nameOf(rivalId)} are rivals.`,
        'good',
      );
    else tell(out.message, 'bad');
  };

  return (
    <div className="fsp-form-fields">
      <Caption>Rivals</Caption>
      <Text size="sm" tone="secondary" block>
        In a rivalry week each team plays its rival, and the teams with none play each other.
        {/* A BYE ONLY WHERE THERE ARE BYES: a league with an odd number of teams. */}
        {teams.length % 2 === 1 ? ' A team with that week’s bye keeps it.' : ''} Picking a team that
        has a rival ends that pairing.
        {canName ? ' A rival is saved for both teams as soon as you pick it.' : ''}
      </Text>
      <div className="fsp-form-placed">
        {teams.map((team) => (
          <div key={team.id} className="fsp-form-placed-row">
            <span className="fsp-form-placed-name">{team.name}</span>
            <Choice
              label={`${team.name}’s rival`}
              hideLabel
              searchable={false}
              value={team.rivalId ?? NONE}
              onChange={(v) => {
                if (v === null) return;
                const next = v === NONE ? null : v;
                if (next !== team.rivalId) void name(team, next);
              }}
              options={[
                { value: NONE, label: 'No rival' },
                ...teams
                  .filter((t) => t.id !== team.id)
                  .map((t) => ({
                    value: t.id,
                    label:
                      t.rivalId !== null && t.rivalId !== team.id
                        ? `${t.name} (now ${nameOf(t.rivalId)}’s)`
                        : t.name,
                  })),
              ]}
              disabled={!canName || naming !== null}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
